/* ARCS — a Social series where each post builds on the one before.
 *
 * Her wizard (decided 2026-09-25, built 2026-09-28; utils/socialArcs.js holds
 * the model and says where everything is stored). The mockup settled WHAT
 * happens: set it up, Brill plans once, the posts are written in order, each
 * from the finished ones before it. The LOOK is the Momentum pattern's, not
 * the mockup's (her note, 2026-09-28: "not sure the arc was fully designed
 * with our new look"):
 *   · the arc is ONE ROW on the Social table (social.jsx, the Arcs band),
 *   · its setup is a pop-up like Draft it for me (`ArcSetup`),
 *   · its screen is its name as the heading and a stack of the shared Block
 *     rows, one per post (`ArcScreen`).
 *
 * Nothing here knows about the hub; it is handed the arc, the posts and what
 * to call. The request for a post is `postRequest` (utils/socialDraft.js), the
 * SAME one the post screen sends — two paths to Brill, one request.
 */
import { useEffect, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { Lightbulb, Plus, Send, Sparkles, Trash2, X } from "lucide-react";
import { SOCIAL_HOOK_CATEGORIES } from "../../constants/socialHookCategories";
import { offerLine } from "../../utils/offerLine";
import { POST_LENGTHS, SOCIAL_PLATFORMS, defaultLength, pictureOf, platformOf } from "../../utils/socialPosts";
import { addUse, isUnused, kindOf, usedByStamp, usedList, useLibrary } from "../../utils/library";
import { postRequest, writtenText } from "../../utils/socialDraft";
import {
  ARC_LENGTHS, MIXED, arcBasisNow, arcContext, arcDrift, arcSendRows, arcRows, arcStates, briefPick, capPicks, clampPostCount, dayLabel, defaultPostCount, parseArcPlan,
  pickLabel, planLibrary, planPictures, slotAngle, slotChoice, slotDates, slotItems, setPictureOnSlot, slotLength, slotPickRows, slotPicture, slotPost, togglePickOnSlot,
  restartArc, sendStart, storyCount, today, togglePick,
} from "../../utils/socialArcs";
import { pickedPicture } from "../../utils/socialDraft";
import { B, btnGhost, input, quickBtn } from "./tokens";
import { Bread, Choice, Field, IconBtn, PageTitleField, StateDots, Status, useTwoPane } from "./ui";
import { BarBack, MomentumBar } from "./shell";
import { Block } from "./block";
import { AskSelect, LengthChoice, LibraryChoices, PictureLine, PictureSource, SOCIAL_HINT, readPostAs, writePostAs } from "./social";
import { useCrmConnected } from "./mediaPicker";

/* What the last post asks, in words Brill plans toward. */
export function askLabelOf(ask, offer, ctas = []) {
  if (!ask || ask === "none") return "no ask — end naturally";
  if (ask === "message") return "comment or message me";
  if (ask === "learn") return `learn more about ${offer?.name || "the offer"} — link in the comments`;
  if (ask.startsWith("link:")) {
    const c = ctas.find((x) => x.id === ask.slice(5));
    const name = c?.name || c?.buttonText || "her link";
    return c?.type === "call" ? `book a call (${name}) — link in the comments` : `go to ${name} — link in the comments`;
  }
  return "no ask — end naturally";
}

/* The pillars this arc mixes: the ones she kept in at setup, else all. */
export const planPillars = (arc, pillars = []) => {
  const w = arc?.themeMix?.mode === "manual" ? Object.keys(arc.themeMix.weights || {}) : null;
  const chosen = w ? pillars.filter((p) => w.includes(p.label)) : pillars;
  return chosen.length ? chosen : pillars;
};

/* THE ONE PLANNING CALL. Resolves to the arc as it should be saved (name, goal
   and slots from Brill), or throws with a sentence she can act on. */
async function requestArcPlan({ session, arc, pillars, offers, ctas, socialContext, library = [] }) {
  const brief = arc.brief || {};
  const count = clampPostCount(brief.count, brief.days);
  const dates = slotDates(arc.startDate, brief.days, count);
  const offer = offers.find((o) => o.id === arc.offerId) || null;
  const use = planPillars(arc, pillars);
  /* Her Library picks and her picture pool, keyed for Brill (L1…, P1…). The
     SAME two lists go to the reader below, so a key can only ever mean the
     thing it was sent as. */
  const picks = planLibrary(arc, library);
  const pool = planPictures(arc);
  const token = await session?.getToken();
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      mode: "social", moduleId: "social_generator", clientContext: socialContext.clientContext, kind: "arc_plan",
      plan: {
        about: brief.about || "", count, days: brief.days, dates: dates.map(dayLabel), platform: platformOf(arc.platform).label,
        offer: offer ? offerLine(offer) : null, askLabel: askLabelOf(brief.ask, offer, ctas), mixed: brief.length === MIXED,
        pillars: use.map((p) => ({ label: p.label, subtitle: p.subtitle, themes: p.themes || [] })),
        library: picks.map(({ key, kind, text, hasPicture }) => ({ key, kind, text, hasPicture })),
        pictures: pool.filter((p) => p.about).map(({ key, about }) => ({ key, about })),
      },
    }),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(res.status === 429 ? "That's the rate limit — give it a moment and plan it again." : e?.error || `Brill couldn't plan it (${res.status}).`);
  }
  const text = (await res.json()).content?.find((b) => b.type === "text")?.text || "";
  const plan = parseArcPlan(text, { count, pillars: use, hookIds: SOCIAL_HOOK_CATEGORIES.map((c) => c.id), finalAsk: brief.ask || "none", dates, library: picks, pictures: pool, mixed: brief.length === MIXED });
  if (plan.error) throw new Error(plan.error);
  return {
    ...arc,
    name: String(arc.name || "").trim() || plan.name || "Untitled arc",
    brief: { ...brief, count, goal: plan.goal, slots: plan.slots },
  };
}


/* ── Setting an arc up ──────────────────────────────────────────────────────
   A pop-up with Cancel, like Draft it for me (rule 8's one Cancel). Days, not
   posts (her call): 3 / 7 / 14, a post every other day by default, the count
   hers to change. Only "what's it about" asks her to think; everything else
   starts on a sensible answer. */
export function ArcSetup({ pillars = [], offers = [], ctas = [], onCancel, onPlan, busy, error }) {
  const [about, setAbout] = useState("");
  const [days, setDays] = useState(7);
  const [count, setCount] = useState(defaultPostCount(7));
  /* Today unless she picks (her call, 2026-09-30). */
  const [start, setStart] = useState(today());
  const [platform, setPlatform] = useState("linkedin");
  const [offerId, setOfferId] = useState("");
  const [ask, setAsk] = useState("none");
  const [length, setLength] = useState(defaultLength("linkedin"));
  /* Every pillar in by default; tapping one leaves it out. Never none — an
     empty mix would mean all of them anyway. */
  const [left, setLeft] = useState([]);
  /* HER LIBRARY, as the arc's source material (decided 2026-09-29): the same
     list the Draft pop-up shows, with checkboxes. `togglePick` holds the rule. */
  const { rows: library } = useLibrary();
  const [picks, setPicks] = useState([]);
  const [libOpen, setLibOpen] = useState(false);
  /* PICTURES FOR THIS ARC — the pool. Each with her line on what is in it,
     which is what Brill matches on. */
  const crm = useCrmConnected();
  const [pics, setPics] = useState([]);
  const [picOpen, setPicOpen] = useState(false);
  const picSeq = useRef(0);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onCancel]);
  const pickDays = (d) => { setDays(d); setCount(defaultPostCount(d)); };
  const dates = start ? slotDates(start, days, count) : [];
  const inMix = pillars.filter((p) => !left.includes(p.label));
  /* Stepping the count down below the stories she ticked keeps the first
     ones; stepping it back up brings the rest back. */
  const kept = capPicks(picks, count);
  const storiesFull = storyCount(kept) >= count;
  const unused = library.filter((i) => isUnused(i)).length;
  const addPic = (link) => {
    const url = String(link || "").trim();
    setPicOpen(false);
    if (!/^https?:\/\/\S+$/i.test(url) || pics.some((p) => p.url === url)) return;
    picSeq.current += 1;
    setPics((cur) => [...cur, { id: `pic_${Date.now().toString(36)}_${picSeq.current}`, type: "photo", url, about: "" }]);
  };
  const undescribed = pics.filter((p) => !String(p.about || "").trim()).length;
  const togglePillar = (label) => setLeft((l) => (l.includes(label) ? l.filter((x) => x !== label) : (inMix.length > 1 ? [...l, label] : l)));
  const submit = () => {
    if (busy || !start) return;
    onPlan({
      about: about.trim(), days, count: clampPostCount(count, days), start, platform,
      offerId: offerId || null, ask: ask === "learn" && !offerId ? "none" : ask, length,
      themeMix: left.length ? { mode: "manual", weights: Object.fromEntries(inMix.map((p) => [p.label, 1])) } : { mode: "auto" },
      library: kept.map(briefPick),
      pictures: pics.slice(0, clampPostCount(count, days)).map((p) => ({ id: p.id, url: p.url, about: String(p.about || "").trim() })),
    });
  };
  const stepBtn = { width: 34, height: 34, borderRadius: 17, border: `1px solid ${B.border2}`, background: B.surface, cursor: "pointer", fontFamily: "inherit", fontSize: 17, color: B.text, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
  return (
    <div role="dialog" aria-modal="true" aria-label="New multi-post arc"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 560, maxHeight: "calc(100vh - 32px)", overflowY: "auto", background: B.surface, borderRadius: 14, border: `1px solid ${B.border2}`, boxShadow: "var(--shadow-menu)", padding: "20px 20px 16px" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 4 }}>New multi-post arc</div>
        <div style={{ fontSize: 12.5, color: B.dim, marginBottom: 14 }}>Brill plans it once, then drafts one post at a time from the ones you've finished.</div>
        <Field label="What's the arc about?">
          <textarea value={about} onChange={(e) => setAbout(e.target.value)} autoFocus rows={3}
            aria-label="What's the arc about?" placeholder="The idea the series carries. A real moment or belief behind it makes a noticeably better arc."
            style={{ ...input, resize: "vertical", lineHeight: 1.5 }} />
        </Field>
        <Field label="From your library">
          {kept.map((it) => (
            <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, padding: "6px 10px", background: B.tealQuiet, border: `1px solid ${B.tealLine}`, borderRadius: 8, fontSize: 12 }}>
              <Lightbulb size={13} color={B.teal} style={{ flexShrink: 0 }} />
              <span style={{ color: B.mid, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <b style={{ fontWeight: 700 }}>{kindOf(it.kind).label}</b> · {pickLabel(it)}
              </span>
              {pickedPicture(it) && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: B.mid, flexShrink: 0 }}>
                  <img src={pickedPicture(it)} alt="" style={{ width: 22, height: 22, objectFit: "cover", borderRadius: 4 }} /> and its picture
                </span>
              )}
              <button type="button" onClick={() => setPicks((cur) => cur.filter((p) => p.id !== it.id))} aria-label={`Leave out ${pickLabel(it)}`} title="Leave this out"
                style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: B.dim, padding: 2, display: "flex", flexShrink: 0 }}><X size={13} /></button>
            </div>
          ))}
          <button type="button" onClick={() => setLibOpen((v) => !v)} aria-expanded={libOpen}
            style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, padding: 0, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: B.teal }}>
            <Lightbulb size={13} /> {libOpen ? "Close your library" : kept.length ? "Add or change" : "From your library"}
            {!libOpen && !kept.length && unused > 0 && <span style={{ color: B.dim, fontWeight: 400 }}>({unused} unused)</span>}
          </button>
          {libOpen && (
            <LibraryChoices library={library} picked={kept.map((p) => p.id)}
              whyNot={(it) => (it.kind === "story" && storiesFull ? `One story per post, and this arc has ${count}. Untick one or add a post.` : null)}
              onPick={(it) => setPicks((cur) => togglePick(capPicks(cur, count), it, count))} />
          )}
          <div style={{ fontSize: 11.5, color: B.dim, lineHeight: 1.5 }}>
            Up to {count} {count === 1 ? "story" : "stories"}, one per post, and one each of an idea, an objection and a testimonial. Brill builds a post around each.
          </div>
        </Field>
        <Field label="How long">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {ARC_LENGTHS.map((d) => <Choice key={d} on={days === d} onClick={() => pickDays(d)}>{d} days</Choice>)}
          </div>
        </Field>
        <Field label="Starts">
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} aria-label="Starts" style={{ ...input, maxWidth: 220 }} />
        </Field>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: B.surface2, border: `1px solid ${B.border}`, borderRadius: 10, marginBottom: 18 }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: B.text, lineHeight: 1.5 }}>
            <b>{count} post{count === 1 ? "" : "s"}</b>
            <span style={{ color: B.dim }}> — {dates.map(dayLabel).join(", ")}</span>
          </div>
          <button type="button" aria-label="One fewer post" disabled={count <= 1} onClick={() => setCount((c) => clampPostCount(c - 1, days))} style={{ ...stepBtn, opacity: count <= 1 ? 0.4 : 1 }}>−</button>
          <button type="button" aria-label="One more post" disabled={count >= days} onClick={() => setCount((c) => clampPostCount(c + 1, days))} style={{ ...stepBtn, opacity: count >= days ? 0.4 : 1 }}>+</button>
        </div>
        <Field label="Platform">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {SOCIAL_PLATFORMS.map((p) => <Choice key={p.id} on={platform === p.id} onClick={() => { setPlatform(p.id); setLength((l) => (l === MIXED ? l : defaultLength(p.id))); }}>{p.label}</Choice>)}
          </div>
        </Field>
        <Field label="Offer it builds to">
          <select value={offerId} onChange={(e) => setOfferId(e.target.value)} aria-label="Offer it builds to" style={input}>
            <option value="">No offer</option>
            {offers.map((o) => <option key={o.id} value={o.id}>{o.name || "Untitled offer"}</option>)}
          </select>
        </Field>
        <Field label="The last post's ask">
          <AskSelect value={ask} onChange={setAsk} hasOffer={!!offerId} ctas={ctas} />
        </Field>
        {pillars.length > 1 && (
          <Field label="Pillars">
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {pillars.map((p) => <Choice key={p.label} on={!left.includes(p.label)} onClick={() => togglePillar(p.label)}>{p.label}</Choice>)}
            </div>
            <div style={{ fontSize: 11.5, color: B.dim, marginTop: 6 }}>{left.length ? "Brill mixes the ones in teal." : "Brill mixes all of them. Tap one to leave it out."}</div>
          </Field>
        )}
        <Field label="Pictures for this arc">
          {pics.map((p) => (
            <PictureLine key={p.id} picture={p} onDone={() => {}}
              onAbout={(v) => setPics((cur) => cur.map((x) => (x.id === p.id ? { ...x, about: v } : x)))}
              onRemove={() => setPics((cur) => cur.filter((x) => x.id !== p.id))} />
          ))}
          {picOpen ? (
            /* Escape closes the picture row, not the whole pop-up with her answers in it. */
            <div onKeyDown={(e) => { if (e.key === "Escape") e.stopPropagation(); }}>
              <PictureSource crm={crm} remember="arc" onAttach={addPic} onCancel={() => setPicOpen(false)} />
            </div>
          ) : pics.length < count ? (
            <button type="button" onClick={() => setPicOpen(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, margin: pics.length ? "8px 0" : "0 0 8px", padding: 0, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: B.teal }}>
              <Plus size={13} /> Add a picture
            </button>
          ) : null}
          <div style={{ fontSize: 11.5, color: undescribed ? B.amber : B.dim, lineHeight: 1.5 }}>
            {undescribed
              ? `${undescribed === 1 ? "One picture has" : `${undescribed} pictures have`} no line yet. Brill matches by what you say is in it, not by looking, so one without a line just takes the next free post.`
              : "A story's or idea's own picture comes with it. These go to the posts without one, matched by your line on what's in each."}
          </div>
        </Field>
        <Field label="Length of each post">
          {/* MIXED (her ask, 2026-09-30): Brill gives each post its own length in
              the plan, and each row says which — hers to change before it is written. */}
          <LengthChoice value={length} onChange={setLength} extra={[{ id: MIXED, label: "Mixed", words: "varies by post" }]} />
        </Field>
        {/* A failed save says so HERE, over her answers — behind the pop-up it
            would be a banner she cannot see, and closing would lose them. */}
        {error && !busy && (
          <div style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "10px 12px", marginBottom: 10, fontSize: 12.5, color: "var(--danger-text)", lineHeight: 1.5 }}>
            <b>The arc didn't save.</b> {error}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
          <button type="button" onClick={onCancel} style={{ ...btnGhost, padding: "8px 14px", fontSize: 13 }}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy || !start}
            style={{ ...quickBtn, padding: "8px 16px", fontSize: 13, fontWeight: 700, opacity: busy || !start ? 0.6 : 1 }}>{busy ? "Saving…" : "Plan my arc"}</button>
        </div>
      </div>
    </div>
  );
}


/* ── The arc ────────────────────────────────────────────────────────────────
   Its name as the heading (rule 7), where and when under it, then one BLOCK
   per post in order — the row every composed record is made of:
     · the SPARKLE drafts that post in place, with the finished posts before
       it in front of Brill. ONLY THE NEXT UNWRITTEN POST'S is live: a post
       written ahead of the one before it could not follow on from it, which is
       the whole of her wizard. The later ones say why, rather than vanishing
       (rule 3).
     · the PENCIL edits the post, and what it is about, right here — and while
       it is unwritten, its pillar, hook and ask.
     · the CHAT opens the full post screen (preview, picture, first comment,
       Send to GHL) with `← The arc` as the one way back.
     · the BIN deletes that post; the plan keeps its place.
   A just-drafted post OPENS, so she reads and corrects it before the next. */
const planStarted = new Set();   // one automatic plan per arc per visit — StrictMode runs effects twice

export function ArcScreen({
  arc, posts = [], pillars = [], offers = [], ctas = [], socialContext = {},
  autoPlan = false, newPostId, onSaveArc, onStorePost, onOpenPost, onDeletePost, onDeleteArc, onBack, confirm, error, onDismissError,
}) {
  const { session } = useSession();
  const [name, setName] = useState(arc?.name || "");
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [busySlot, setBusySlot] = useState(null);
  const [draftError, setDraftError] = useState(null);
  const [justWritten, setJustWritten] = useState(null);
  /* Going through the plan: the posts to step through, or null. It opens by
     itself when a plan lands, and from the button under the posts after. */
  const [walk, setWalk] = useState(null);
  /* Send the arc to GHL: only where a CRM is connected (the post screen's rule). */
  const crm = useCrmConnected();
  const [sendOpen, setSendOpen] = useState(false);
  const latestArc = useRef(arc);
  latestArc.current = arc;
  /* Her Library, live: a post's picks are read when it is DRAFTED, so a story
     she has since corrected reaches Brill corrected. The shared load. */
  const { rows: library, loading: libraryLoading, save: persistItem } = useLibrary();
  /* A plan or a draft that needs her picks WAITS for them. Sent while the
     Library is still arriving, it would go without her stories and look fine. */
  const libraryBusy = useRef(libraryLoading);
  libraryBusy.current = libraryLoading;
  const LIBRARY_WAIT = "Your Library is still loading, and this needs what you picked from it. Try again in a moment.";
  const latestLibrary = useRef(library);
  latestLibrary.current = library;
  const startPost = (slot, a = latestArc.current) => slotPost(a, slot, newPostId(), latestLibrary.current);
  /* The name arrives from Brill's plan after mount, so it follows the stored
     one until she has typed her own. */
  const typed = useRef(false);
  useEffect(() => { if (!typed.current) setName(arc?.name || ""); }, [arc?.name]);

  const hasPlan = !!(arc?.brief?.slots || []).length;
  const plan = async () => {
    const cur = latestArc.current;
    if (!cur) return;
    if (libraryBusy.current && (cur.brief?.library || []).length) { setPlanError(LIBRARY_WAIT); return; }
    setPlanning(true);
    setPlanError(null);
    try {
      const planned = await requestArcPlan({ session, arc: cur, pillars, offers, ctas, socialContext, library: latestLibrary.current });
      const saved = await onSaveArc(planned);
      if (!saved) setPlanError("Brill planned it, but the arc didn't save — see the note above.");
      else setWalk((planned.brief?.slots || []).map((x) => x.n));
    } catch (err) {
      setPlanError(err?.message || "I couldn't reach Brill just then.");
    } finally {
      setPlanning(false);
    }
  };
  /* The latest `plan`, for the effect below — it closes over this render's
     props, and the effect must run on arrival only, not on every render. */
  const planRef = useRef(plan);
  planRef.current = plan;
  const arcId = arc?.id;
  useEffect(() => {
    if (!autoPlan || !arcId || hasPlan || planStarted.has(arcId)) return;
    planStarted.add(arcId);
    planRef.current();
  }, [autoPlan, arcId, hasPlan]);

  /* ON A PHONE THE ROW'S HEADER CARRIES ONLY "POST 3" (her catch, 2026-09-30:
     the labels squeezed to "POST…" and "Not…"). A 451px header has room for
     the label, the word count and four actions, not a date and a topic too —
     so the date and the topic go on their own line under the row, where the
     topic wraps and is read whole. Wide, all of it stays in the header, with
     the topic taking only what is left over. */
  const wide = useTwoPane(640);
  if (!arc) return null;
  const { rows, written, total, next } = arcRows(arc, posts);
  const offer = offers.find((o) => o.id === arc.offerId) || null;
  const platform = platformOf(arc.platform);
  const mix = planPillars(arc, pillars);

  const saveSlot = (n, patch) => {
    const cur = latestArc.current;
    const slots = (cur.brief?.slots || []).map((s) => {
      if (s.n === n) return { ...s, ...patch };
      /* A pool picture is on ONE post: giving it to this one takes it off the
         unwritten post that had it. A written post's picture is its own by
         then (post.visual) and is never touched from here. */
      if (patch.picture && s.picture === patch.picture && !rows.find((r) => r.slot.n === s.n)?.written) return { ...s, picture: "" };
      return s;
    });
    return onSaveArc({ ...cur, brief: { ...cur.brief, slots } });
  };

  /* THE SPARKLE. The post it writes is created here if the slot has none yet
     — nothing reaches the database before it has words. */
  /* `fresh` is the arc as the walkthrough just left it — its last taps may
     still be on their way to the database, so the draft reads THEM, not the
     arc from the last render. */
  const draftSlot = async (row, fresh = null) => {
    if (busySlot) return;
    const cur = fresh || latestArc.current;
    const slot = (cur.brief?.slots || []).find((x) => x.n === row.slot.n) || row.slot;
    if (libraryBusy.current && (slot.picks || []).length) { setDraftError(LIBRARY_WAIT); return; }
    const base = row.post || startPost(slot, cur);
    const items = slotItems(slot, latestLibrary.current);
    setBusySlot(row.slot.n);
    setDraftError(null);
    try {
      const { body, patchFor } = postRequest({
        draft: base, choice: slotChoice(cur, slot), angle: slotAngle(slot, latestLibrary.current), pillars, offers, ctas, posts,
        arc: arcContext(cur, posts, row.slot.n),
      });
      const token = await session?.getToken();
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ mode: "social", moduleId: "social_generator", clientContext: socialContext.clientContext, ...body }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(res.status === 429 ? "That's the rate limit — give it a moment. Nothing was changed." : e?.error || `Error ${res.status}`);
      }
      const words = writtenText(await res.json());
      if (!words) { setDraftError(`Brill came back empty on post ${row.slot.n}. Try again, or open it and write it in the chat.`); return; }
      const { _idea, ...patch } = patchFor(words)(base);
      const saved = await onStorePost({ ...base, ...patch });
      if (saved) {
        setJustWritten((j) => ({ n: row.slot.n, stamp: (j?.stamp || 0) + 1 }));
        /* Stamped USED BY THIS POST when its words land, as a single post's
           pick is — once per post, so a rewrite is not a second use. */
        items.filter((it) => !usedList(it).some((u) => u.type === "post" && u.id === base.id))
          .forEach((it) => persistItem({ ...it, usedBy: addUse(it, usedByStamp("post", base.id, saved.title || patch.title || null)) }));
      }
    } catch (err) {
      setDraftError(err?.message || "I couldn't reach Brill just then. Nothing was changed.");
    } finally {
      setBusySlot(null);
    }
  };
  /* A rewrite has no undo, so a post with words asks first; an empty one does
     not — the line Sites, Course and Outreach draw. */
  const askDraft = (row) => {
    if (!row.written) { draftSlot(row); return; }
    confirm({
      title: `Rewrite post ${row.slot.n}?`,
      message: "Brill writes it again from the posts before it. What's in it now is replaced, and there's no undo. The posts after it are not changed.",
      confirmLabel: "Rewrite it",
      danger: true,
      onConfirm: async () => { confirm(null); await draftSlot(row); },
    });
  };

  const kindFor = (row) => ({
    id: "arc_post",
    label: `Post ${row.slot.n}${wide && row.slot.date ? ` · ${dayLabel(row.slot.date)}` : ""}`,
    fields: [
      { key: "topic", label: "What it's about", type: "text", guide: "this post's job in the series" },
      { key: "caption", label: "The post", type: "prose", guide: "" },
    ],
  });
  const commitRow = (row) => async (content) => {
    if (String(content.topic || "") !== String(row.slot.topic || "")) await saveSlot(row.slot.n, { topic: String(content.topic || "").trim() });
    if (String(content.caption || "") !== String(row.post?.caption || "")) {
      const base = row.post || startPost(row.slot);
      await onStorePost({ ...base, caption: content.caption || "" });
    }
  };
  const nextLabel = next ? `Write post ${next.slot.n}${next.slot.n > 1 ? ` — it builds on post ${next.slot.n - 1}` : ""}` : null;

  return (
    <>
      <Bread text="Grow" crumb="Social" hint={SOCIAL_HINT} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <PageTitleField value={name} placeholder="Name this arc" label="Arc name"
          onChange={(v) => { typed.current = true; setName(v); }}
          onCommit={() => { if (name.trim() !== String(arc.name || "")) onSaveArc({ ...latestArc.current, name: name.trim() }); }} />
        <IconBtn icon={Trash2} label="Delete arc" onClick={() => onDeleteArc(arc)} size={15} hover={B.salmon} />
      </div>
      <div style={{ fontSize: 12.5, color: B.dim, margin: "0 0 18px 2px", lineHeight: 1.6 }}>
        {platform.label} · {arc.brief?.days || "?"} days from {dayLabel(arc.startDate)}{offer ? ` · builds to ${offer.name || "your offer"}` : ""}
      </div>
      <MomentumBar levels={2} lead={<BarBack label="← All posts" onClick={onBack} />}
        extras={hasPlan ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <StateDots states={arcStates(arc, posts)} noun="post" />
            <span style={{ fontSize: 12.5, color: written === total ? B.teal : B.dim, fontWeight: 600 }}>{written} of {total} written</span>
            {crm && written > 0 && (
              <button type="button" onClick={() => setSendOpen(true)} disabled={!!busySlot}
                style={{ ...btnGhost, padding: "6px 12px", fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Send size={13} /> Send to GHL
              </button>
            )}
          </span>
        ) : null} />

      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 14, fontSize: 13, color: "var(--danger-text)", cursor: "pointer", lineHeight: 1.55 }}>
          <b>That didn't save.</b> {error}
        </div>
      )}

      {!hasPlan ? (
        <div style={{ border: `1px dashed ${planError ? B.amberLine : B.border2}`, borderRadius: 12, padding: "22px 20px", textAlign: "center" }}>
          {planning ? (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 9, fontSize: 13.5, color: B.teal }}>
              <Sparkles size={15} /> Brill is planning your arc…
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13.5, color: planError ? B.amber : B.dim, marginBottom: 12, lineHeight: 1.55 }}>
                {planError || "Brill hasn't planned this arc yet."}
              </div>
              <button type="button" onClick={plan} style={{ ...quickBtn, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Sparkles size={13} /> {planError ? "Plan it again" : "Plan it"}
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          {draftError && (
            <div style={{ fontSize: 12.5, color: B.amber, marginBottom: 10, lineHeight: 1.5 }}>{draftError}</div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map((row) => {
              const isNext = !!next && next.slot.n === row.slot.n;
              const waiting = !row.written && !isNext;
              const busy = busySlot === row.slot.n;
              const opened = justWritten?.n === row.slot.n;
              return (
                /* Keyed by what it holds, so a post landing from the sparkle
                   remounts the row open with its words (the Block's own state
                   is initialised once). */
                <div key={`${row.slot.n}-${row.post?.id || "none"}-${opened ? justWritten.stamp : 0}`}>
                <Block
                  kind={kindFor(row)} noun="post" startOpen={opened}
                  content={{ topic: row.slot.topic || "", caption: row.post?.caption || "" }}
                  words={(c) => String(c.caption || "").trim().split(/\s+/).filter(Boolean).length}
                  onCommit={commitRow(row)}
                  sparkle={{
                    onClick: () => askDraft(row), busy,
                    disabled: waiting || (!!busySlot && !busy),
                    /* Said, not hidden (rule 3): why this one waits. */
                    label: waiting ? `Write post ${next?.slot.n} first — each post builds on the ones before it` : row.written ? "Rewrite with Brill" : "Write with Brill",
                  }}
                  onChat={() => onOpenPost(row.post || startPost(row.slot))}
                  onClear={row.post ? () => onDeletePost(row.post) : undefined} clearLabel="Delete this post"
                  badges={row.written ? <Status cls={row.post.status === "done" ? "locked" : "draft"} text={row.post.status === "done" ? "Done" : "Draft"} /> : null}
                  tag={wide ? (
                    /* Basis 0: the topic takes what the label and count leave,
                       instead of all three shrinking together. */
                    <span style={{ flex: "1 1 0", fontSize: 12, color: B.dim, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.slot.topic}
                    </span>
                  ) : null}
                  editTop={!row.written ? (
                    <SlotSettings slot={row.slot} pillars={mix} ctas={ctas} hasOffer={!!arc.offerId} last={row.slot.n === total}
                      pool={arc.brief?.pictures || []} slots={arc.brief?.slots || []} mixed={arc.brief?.length === MIXED}
                      bringsOwn={slotPicture(arc, { ...row.slot, picture: "" }, library)?.from || null}
                      onChange={(patch) => saveSlot(row.slot.n, patch)} />
                  ) : null}
                />
                {/* WHICH STORY AND WHICH PICTURE WENT WHERE, on the plan itself.
                    Before the post exists the picture is the one it will start
                    with; after, it is the post's own — the one place it lives. */}
                <SlotSources lead={wide ? null : [row.slot.date ? dayLabel(row.slot.date) : "", row.slot.topic].filter(Boolean).join(" · ")}
                  length={arc.brief?.length === MIXED || row.slot.length ? (row.post?.inputs?.length || slotLength(arc, row.slot)) : null}
                  picks={slotPickRows(arc, row.slot, library)}
                  picture={row.post ? pictureOf(row.post.visual) : slotPicture(arc, row.slot, library)} />
                <DriftNote changed={arcDrift(arc, posts, row.slot.n)} busy={!!busySlot}
                  onRewrite={() => askDraft(row)}
                  onDismiss={() => onStorePost({ ...row.post, inputs: { ...(row.post.inputs || {}), arcBasis: arcBasisNow(arc, posts, row.slot.n) } })} />
                </div>
              );
            })}
          </div>
          {next && (
            <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <button type="button" onClick={() => draftSlot(next)} disabled={!!busySlot}
                style={{ ...quickBtn, fontWeight: 700, padding: "9px 16px", display: "inline-flex", alignItems: "center", gap: 7, opacity: busySlot ? 0.6 : 1 }}>
                <Sparkles size={14} /> {busySlot ? `Brill is writing post ${busySlot}…` : nextLabel}
              </button>
              <button type="button" onClick={() => setWalk(rows.filter((r) => !r.written).map((r) => r.slot.n))} disabled={!!busySlot}
                style={{ ...btnGhost, padding: "9px 14px", fontSize: 13 }}>Go through the plan</button>
            </div>
          )}
          {sendOpen && <ArcSend arc={arc} posts={posts} onSaveArc={onSaveArc} onStorePost={onStorePost} onClose={() => setSendOpen(false)} />}
          {walk && walk.length > 0 && (
            <ArcWalkthrough arc={arc} steps={walk} library={library} pillars={pillars} ctas={ctas} rows={rows}
              onSaveArc={onSaveArc} onClose={() => setWalk(null)}
              /* HER READ, 2026-09-30: after the choices "it seems like it would
                 have something". It ends on writing the NEXT post — one post,
                 one message, the wizard's rule — and it opens with its words. */
              finishLabel={next ? `Write post ${next.slot.n}` : null} finishN={next ? next.slot.n : null}
              onFinish={next ? (fresh) => { setWalk(null); draftSlot(next, fresh); } : null} />
          )}
        </>
      )}
    </>
  );
}

/* ── Sending the arc to GHL ─────────────────────────────────────────────────
   Every written post, as a DRAFT on its planned day, at one time she picks
   (2026-09-30). Brill still never posts: nothing leaves her planner until she
   schedules it there. One post per request through the same job the post
   screen's Send uses, so a post sent before is UPDATED, never duplicated, and
   one she has already scheduled in GHL is refused, not pulled back. What
   happens to each post is said before she presses, and after, row by row. */
const STOPS_ALL = new Set(["connection", "accounts", "users"]);

export function ArcSend({ arc, posts = [], onSaveArc, onStorePost, onClose }) {
  const { session } = useSession();
  const platform = platformOf(arc.platform);
  const [time, setTime] = useState("09:00");
  const [start, setStart] = useState(() => sendStart(arc));
  const [accounts, setAccounts] = useState(null);
  const [accError, setAccError] = useState("");
  const [postAs, setPostAs] = useState(() => readPostAs(platform.id));
  const [results, setResults] = useState({});
  const [sending, setSending] = useState(false);
  const [finished, setFinished] = useState(false);
  const call = async (body) => {
    const token = await session?.getToken();
    const res = await fetch("/api/ghl", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ job: "social", ...body }),
    });
    return res.json().catch(() => ({ ok: false, error: `GHL didn't answer (${res.status}).` }));
  };
  /* Her accounts, on opening: which ones "Post as" can offer. (Not guarded
     by a ref — StrictMode runs this twice in development, and a ref would let
     the first, cancelled run swallow the second.) */
  useEffect(() => {
    if (!session) return undefined;
    let gone = false;
    (async () => {
      try {
        const token = await session.getToken();
        const res = await fetch("/api/ghl", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ job: "social", action: "accounts" }),
        });
        const r = await res.json().catch(() => ({}));
        if (gone) return;
        if (r.ok) setAccounts(r.accounts || []);
        else { setAccounts([]); setAccError(r.error || "Couldn't read your GHL accounts."); }
      } catch (err) { if (!gone) { setAccounts([]); setAccError(`Could not reach GHL — ${err.message}`); } }
    })();
    return () => { gone = true; };
  }, [session]);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape" && !sending) onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose, sending]);

  const mine = (accounts || []).filter((a) => a.platform === platform.id);
  const chosen = [postAs, mine.length === 1 ? mine[0].id : ""].find((id) => id && mine.some((a) => a.id === id)) || "";
  const rows = arcSendRows(arc, posts, { time, start });
  const moved = start !== String(arc.startDate || "").slice(0, 10);
  const going = rows.filter((r) => r.action !== "skip");
  const ready = accounts !== null && !accError && mine.length > 0 && !!chosen && going.length > 0;

  const send = async () => {
    if (!ready || sending) return;
    setSending(true);
    writePostAs(platform.id, chosen);
    /* A new start moves the ARC first, so the arc screen shows the days GHL
       gets. If that save fails, nothing is sent: two different sets of days
       is worse than none. */
    if (moved && !(await onSaveArc(restartArc(arc, start)))) {
      setSending(false);
      setResults(Object.fromEntries(going.map((r) => [r.n, { error: "Not sent — the arc's new days didn't save. Try again." }])));
      return;
    }
    let stopped = null;
    for (const r of going) {
      if (stopped) { setResults((x) => ({ ...x, [r.n]: { error: "Not sent — see above." } })); continue; }
      setResults((x) => ({ ...x, [r.n]: { busy: true } }));
      try {
        const res = await call({ postId: r.post.id, accountId: chosen, ...(r.at ? { scheduleAt: r.at } : {}) });
        if (!res.ok) {
          setResults((x) => ({ ...x, [r.n]: { error: res.error || "GHL didn't take it." } }));
          if (STOPS_ALL.has(res.stage)) stopped = res.stage;
          continue;
        }
        /* The job wrote the GHL id onto the row; the store has to know it too,
           or her next edit saves over it and the next send makes a duplicate. */
        /* Sent is Done (her call, 2026-10-06) — the arc's dots fill as it goes. */
        await onStorePost({ ...r.post, scheduledDate: r.date, status: "done", inputs: { ...(r.post.inputs || {}), ...(res.ghlPostId ? { ghlPostId: res.ghlPostId } : {}), ghlAccountId: chosen } });
        setResults((x) => ({ ...x, [r.n]: { ok: res.done === "updated" ? "Draft updated" : "Draft made", dated: !!res.scheduleDate } }));
      } catch (err) {
        setResults((x) => ({ ...x, [r.n]: { error: `Could not reach GHL — ${err.message}` } }));
      }
    }
    setSending(false);
    setFinished(true);
  };

  const sent = Object.values(results).filter((r) => r.ok).length;
  const failed = Object.values(results).filter((r) => r.error).length;
  const say = (r) => {
    const res = results[r.n];
    if (res?.busy) return { text: "Sending…", color: B.teal };
    if (res?.error) return { text: res.error, color: B.amber };
    if (res?.ok) return { text: `${res.ok}${res.dated ? `, on ${dayLabel(r.date)}` : ", no date"}`, color: B.teal };
    if (r.action === "skip") return { text: "Not written yet — stays here", color: B.faint };
    if (r.action === "undated") return r.date === today()
      ? { text: "Today — that time has passed, so it lands now", color: B.amber }
      : { text: "Its day has passed — goes without a date", color: B.amber };
    return { text: `${r.post?.inputs?.ghlPostId ? "Updates its draft" : "New draft"}, ${dayLabel(r.date)}`, color: B.dim };
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Send the arc to GHL"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 520, maxHeight: "calc(100vh - 32px)", overflowY: "auto", background: B.surface, borderRadius: 14, border: `1px solid ${B.border2}`, boxShadow: "var(--shadow-menu)", padding: "20px 20px 16px" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 4 }}>Send the arc to GHL</div>
        <div style={{ fontSize: 12.5, color: B.dim, marginBottom: 16, lineHeight: 1.5 }}>
          As drafts in your Social Planner, spread from the day it starts{arc.brief?.days ? ` across ${arc.brief.days} days` : ""}. Nothing posts until you schedule it there.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 16 }}>
          <label>
            <span style={{ display: "block", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>Starts</span>
            <input type="date" value={start} min={today()} onChange={(e) => e.target.value && setStart(e.target.value < today() ? today() : e.target.value)}
              disabled={sending || finished} aria-label="Starts" style={{ ...input, maxWidth: 180 }} />
          </label>
          <label>
            <span style={{ display: "block", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>Time on each day</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} disabled={sending || finished} aria-label="Time on each day" style={{ ...input, maxWidth: 160 }} />
          </label>
          {mine.length > 1 && (
            <label>
              <span style={{ display: "block", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>Post as</span>
              <select value={chosen} onChange={(e) => setPostAs(e.target.value)} disabled={sending || finished} aria-label="Post as" style={input}>
                {!chosen && <option value="">Pick an account</option>}
                {mine.map((a) => <option key={a.id} value={a.id}>{a.name || platform.label}{a.type ? ` (${a.type})` : ""}</option>)}
              </select>
            </label>
          )}
        </div>

        {accounts === null && <div style={{ fontSize: 12.5, color: B.dim, marginBottom: 12 }}>Checking your GHL accounts…</div>}
        {accError && <div style={{ fontSize: 12.5, color: B.amber, marginBottom: 12, lineHeight: 1.5 }}>{accError}</div>}
        {accounts !== null && !accError && !mine.length && (
          <div style={{ fontSize: 12.5, color: B.amber, marginBottom: 12, lineHeight: 1.5 }}>No {platform.label} account is connected in your GHL Social Planner, or its connection has expired. Connect it there, then send.</div>
        )}

        <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, marginBottom: 14 }}>
          {rows.map((r, k) => {
            const s = say(r);
            return (
              <div key={r.n} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "9px 12px", borderTop: k ? `1px solid ${B.border}` : "none", opacity: r.action === "skip" ? 0.6 : 1 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: B.text, flexShrink: 0 }}>Post {r.n}</span>
                <span style={{ fontSize: 12.5, color: s.color, minWidth: 0, lineHeight: 1.45 }}>{s.text}</span>
              </div>
            );
          })}
        </div>

        {finished && (
          <div style={{ fontSize: 12.5, color: failed ? B.amber : B.teal, marginBottom: 12, lineHeight: 1.5 }}>
            {sent ? `${sent} draft${sent === 1 ? "" : "s"} in your GHL Social Planner. Schedule ${sent === 1 ? "it" : "them"} there.` : "Nothing was sent."}
            {failed ? ` ${failed} didn't go — the reason is beside ${failed === 1 ? "it" : "each"}.` : ""}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {finished ? (
            <button type="button" onClick={onClose} style={{ ...quickBtn, padding: "8px 16px", fontSize: 13, fontWeight: 700 }}>Close</button>
          ) : (
            <>
              <button type="button" onClick={onClose} disabled={sending} style={{ ...btnGhost, padding: "8px 14px", fontSize: 13 }}>Cancel</button>
              <button type="button" onClick={send} disabled={!ready || sending}
                style={{ ...quickBtn, padding: "8px 16px", fontSize: 13, fontWeight: 700, opacity: !ready || sending ? 0.6 : 1 }}>
                {sending ? "Sending…" : going.length ? `Send ${going.length} draft${going.length === 1 ? "" : "s"}` : "Nothing written to send"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Going through the plan ─────────────────────────────────────────────────
   Her call, 2026-09-30: after Brill plans, she steps through the posts ONE AT
   A TIME and says which post has what — "make sure it feels really simple".
   So each step is one post and only what that post carries, in the order she
   would think about it: what it's about, what it's built on, its picture, how
   long, then pillar / hook / ask. A section with nothing to choose is not
   drawn. Every tap saves to the plan (rule 1) — there is no Save, and closing
   loses nothing. Only posts still to write are stepped through: a written one
   is changed in the post itself. Nothing here calls Brill. */
/* The note on the walkthrough's last step: Brill writes the next post now;
   each one after is its sparkle, written from what she kept in the one before;
   and her Library picks are already in their posts. */
export function finishNote(n, total, hasPicks) {
  const more = n < total;
  return [
    <b key="a">Brill writes post {n} now.</b>,
    more ? ` Make it yours, then press the sparkle on post ${n + 1}: Brill writes it from what you kept in post ${n}${n + 1 < total ? `, and so on to post ${total}` : ""}.` : " It's the last one in the arc.",
    hasPicks ? " The stories and ideas you picked are already in their posts." : "",
  ];
}

export function ArcWalkthrough({ arc, steps = [], library = [], pillars = [], ctas = [], rows = [], onSaveArc, onClose, finishLabel = null, finishN = null, onFinish = null }) {
  const [i, setI] = useState(0);
  const [slots, setSlots] = useState(arc?.brief?.slots || []);
  const latest = useRef(slots);
  const arcNow = useRef(arc);
  arcNow.current = arc;
  const n = steps[i];
  const slot = slots.find((s) => s.n === n) || null;
  const [topic, setTopic] = useState(slot?.topic || "");
  useEffect(() => { setTopic((latest.current.find((s) => s.n === n) || {}).topic || ""); }, [n]);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const write = (next) => {
    latest.current = next;
    setSlots(next);
    const cur = arcNow.current;
    onSaveArc({ ...cur, brief: { ...cur.brief, slots: next } });
  };
  const patch = (p) => write(latest.current.map((s) => (s.n === n ? { ...s, ...p } : s)));
  const keepTopic = () => {
    const t = topic.trim();
    if (t && t !== String(slot?.topic || "")) patch({ topic: t });
  };
  const go = (to) => { keepTopic(); if (to < 0 || to >= steps.length) onClose(); else setI(to); };
  if (!slot) return null;

  const view = { ...arc, brief: { ...arc.brief, slots } };
  const kept = arc.brief?.library || [];
  const kindOfId = (id) => kept.find((p) => p.id === id)?.kind || null;
  const writtenN = new Set(rows.filter((r) => r.written).map((r) => r.slot.n));
  const holder = (test) => slots.find((s) => s.n !== n && test(s));
  const pool = (arc.brief?.pictures || []).filter((p) => p && p.url);
  const own = slotPicture(view, { ...slot, picture: "" }, library);   // what its story or idea brings
  const len = slotLength(view, slot) || defaultLength(arc.platform);
  const date = slot.date ? dayLabel(slot.date) : "";
  const head = { fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 8 };
  const section = { marginBottom: 18 };
  const chip = (on, off) => ({
    display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 16, fontSize: 12.5, fontFamily: "inherit", textAlign: "left",
    cursor: off ? "not-allowed" : "pointer", opacity: off ? 0.5 : 1, maxWidth: "100%",
    border: `1px solid ${on ? B.teal : B.border2}`, background: on ? B.tealQuiet : "transparent", color: on ? B.teal : B.mid, fontWeight: on ? 700 : 500,
  });
  const tile = (on) => ({
    display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 76, padding: 5, borderRadius: 10, cursor: "pointer", fontFamily: "inherit",
    border: `2px solid ${on ? B.teal : B.border}`, background: on ? B.tealQuiet : "transparent",
  });
  const last = i === steps.length - 1;

  return (
    <div role="dialog" aria-modal="true" aria-label="Go through the plan"
      onMouseDown={(e) => { if (e.target === e.currentTarget) { keepTopic(); onClose(); } }}
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 520, maxHeight: "calc(100vh - 32px)", display: "flex", flexDirection: "column", background: B.surface, borderRadius: 14, border: `1px solid ${B.border2}`, boxShadow: "var(--shadow-menu)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 20px 12px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: B.text }}>Post {slot.n} <span style={{ color: B.dim, fontWeight: 500 }}>of {rows.length}</span></div>
            {date && <div style={{ fontSize: 12.5, color: B.dim, marginTop: 2 }}>{date}</div>}
          </div>
          <IconBtn icon={X} label="Close" onClick={() => { keepTopic(); onClose(); }} size={16} />
        </div>

        <div style={{ padding: "4px 20px 6px", overflowY: "auto" }}>
          <div style={section}>
            <div style={head}>What it's about</div>
            <textarea value={topic} onChange={(e) => setTopic(e.target.value)} onBlur={keepTopic} rows={3} aria-label="What it's about"
              style={{ ...input, resize: "vertical", lineHeight: 1.5 }} />
          </div>

          {kept.length > 0 && (
            <div style={section}>
              <div style={head}>Built on</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {kept.map((p) => {
                  const on = (slot.picks || []).includes(p.id);
                  const at = holder((s) => (s.picks || []).includes(p.id));
                  const locked = !!at && writtenN.has(at.n);
                  return (
                    <button key={p.id} type="button" disabled={locked} aria-pressed={on}
                      title={locked ? `Post ${at.n} is written with it` : undefined}
                      onClick={() => write(togglePickOnSlot(latest.current, n, p.id, kindOfId))} style={chip(on, locked)}>
                      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <b>{kindOf(p.kind).label}</b> · {p.label}
                      </span>
                      {!on && <span style={{ color: B.faint, fontWeight: 400, flexShrink: 0 }}>{at ? `post ${at.n}` : "not used"}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(pool.length > 0 || own) && (
            <div style={section}>
              <div style={head}>Picture</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {/* The first tile is what the post gets with no pool picture:
                    its story's own, or nothing. */}
                <button type="button" aria-pressed={!slot.picture} onClick={() => write(setPictureOnSlot(latest.current, n, ""))} style={tile(!slot.picture)}>
                  {own
                    ? <img src={own.url} alt="" style={{ width: 62, height: 62, objectFit: "cover", borderRadius: 6 }} />
                    : <span style={{ width: 62, height: 62, borderRadius: 6, background: B.surface2, display: "flex", alignItems: "center", justifyContent: "center", color: B.faint }}><X size={18} /></span>}
                  <span style={{ fontSize: 11, color: B.mid }}>{own ? `Its ${own.from}'s` : "None"}</span>
                </button>
                {pool.map((p, k) => {
                  const on = slot.picture === p.id;
                  const at = holder((s) => s.picture === p.id);
                  const locked = !!at && writtenN.has(at.n);
                  return (
                    <button key={p.id} type="button" aria-pressed={on} disabled={locked} title={p.about || `Picture ${k + 1}`}
                      onClick={() => write(setPictureOnSlot(latest.current, n, p.id))} style={{ ...tile(on), opacity: locked ? 0.5 : 1, cursor: locked ? "not-allowed" : "pointer" }}>
                      <img src={p.url} alt="" style={{ width: 62, height: 62, objectFit: "cover", borderRadius: 6 }} />
                      <span style={{ fontSize: 11, color: on ? B.teal : B.faint, maxWidth: 66, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{on ? "This post" : at ? `post ${at.n}` : "Not used"}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={section}>
            <div style={head}>How long</div>
            <div role="radiogroup" aria-label="How long" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {POST_LENGTHS.map((l) => (
                <button key={l.id} type="button" role="radio" aria-checked={len === l.id} onClick={() => patch({ length: l.id })} style={chip(len === l.id)}>
                  {l.label} <span style={{ fontWeight: 400, color: B.faint }}>{l.words}</span>
                </button>
              ))}
            </div>
          </div>

          <SlotSettings slot={slot} pillars={planPillars(arc, pillars)} ctas={ctas} hasOffer={!!arc.offerId} last={slot.n === rows.length}
            onChange={(p) => patch(p.pillar !== undefined ? { ...p, theme: "" } : p)} />
        </div>

        {/* WHAT HAPPENS NEXT, said where she is about to press (her ask,
            2026-09-30: "it should probably say that in the wizard"). Only on
            the last step, and only while there is a post to write. */}
        {last && onFinish && (
          <div style={{ margin: "0 20px 12px", padding: "10px 12px", background: B.tealQuiet, border: `1px solid ${B.tealLine}`, borderRadius: 10, fontSize: 12.5, color: B.mid, lineHeight: 1.55 }}>
            {finishNote(finishN, rows.length, kept.length > 0)}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 20px 16px", borderTop: `1px solid ${B.border}` }}>
          <button type="button" onClick={() => go(i - 1)} disabled={i === 0}
            style={{ ...btnGhost, padding: "8px 14px", fontSize: 13, opacity: i === 0 ? 0.4 : 1, cursor: i === 0 ? "default" : "pointer" }}>← Back</button>
          <div aria-hidden="true" style={{ flex: 1, display: "flex", justifyContent: "center", gap: 5 }}>
            {!(last && onFinish) && steps.map((s, k) => <span key={s} style={{ width: 7, height: 7, borderRadius: 4, background: k === i ? B.teal : B.border2 }} />)}
          </div>
          {last && onFinish ? (
            <>
              <button type="button" onClick={() => go(i + 1)} style={{ ...btnGhost, padding: "8px 12px", fontSize: 13 }}>Later</button>
              <button type="button" onClick={() => { keepTopic(); const cur = arcNow.current; onFinish({ ...cur, brief: { ...cur.brief, slots: latest.current } }); }}
                style={{ ...quickBtn, padding: "8px 14px", fontSize: 13, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Sparkles size={13} /> {finishLabel}
              </button>
            </>
          ) : (
            <button type="button" onClick={() => go(i + 1)}
              style={{ ...quickBtn, padding: "8px 16px", fontSize: 13, fontWeight: 700 }}>{last ? "Done" : "Next →"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* A planned post's pillar, hook and ask — Brill's suggestion, hers to change
   before it is written. Above the fields while editing; saved on the press. */
function SlotSettings({ slot, pillars = [], ctas = [], hasOffer, last, pool = [], slots = [], bringsOwn = null, mixed = false, onChange }) {
  const sel = { ...input, padding: "7px 10px", fontSize: 12.5 };
  const head = { display: "block", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 14 }}>
      <label>
        <span style={head}>Pillar</span>
        <select value={slot.pillar || ""} onChange={(e) => onChange({ pillar: e.target.value, theme: "" })} style={sel}>
          <option value="">Let Brill pick</option>
          {pillars.map((p) => <option key={p.label} value={p.label}>{p.label}</option>)}
        </select>
      </label>
      <label>
        <span style={head}>Hook style</span>
        <select value={slot.hook || ""} onChange={(e) => onChange({ hook: e.target.value })} style={sel}>
          <option value="">Let Brill pick</option>
          {SOCIAL_HOOK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </label>
      <div>
        <span style={head}>The ask</span>
        {/* The last post carries her ask; the ones before it may only invite a
            reply (parseArcPlan draws the same line) — never a sale mid-series. */}
        {last
          ? <AskSelect value={slot.ask || "none"} onChange={(v) => onChange({ ask: v })} hasOffer={hasOffer} ctas={ctas} />
          : (
            <select value={slot.ask === "message" ? "message" : "none"} onChange={(e) => onChange({ ask: e.target.value })} style={sel} aria-label="The ask">
              <option value="none">No ask — end naturally</option>
              <option value="message">Comment or message me</option>
            </select>
          )}
      </div>
      {/* A mixed arc's post has its own length; any other arc has one for all. */}
      {mixed && (
        <label>
          <span style={head}>Length</span>
          <select value={slot.length || "medium"} onChange={(e) => onChange({ length: e.target.value })} style={sel} aria-label="Length">
            {POST_LENGTHS.map((l) => <option key={l.id} value={l.id}>{l.label} · {l.words}</option>)}
          </select>
        </label>
      )}
      {/* Only when the arc HAS a pool. A post that does not exist yet starts
          with this picture; once written, its picture is changed in the post. */}
      {pool.length > 0 && (
        <label>
          <span style={head}>Picture</span>
          <select value={pool.some((p) => p.id === slot.picture) ? slot.picture : ""} onChange={(e) => onChange({ picture: e.target.value })} style={sel} aria-label="Picture">
            <option value="">{bringsOwn ? `The one its ${bringsOwn} brings` : "No picture"}</option>
            {pool.map((p, i) => {
              const on = slots.find((x) => x.picture === p.id && x.n !== slot.n);
              const name = String(p.about || "").trim();
              return <option key={p.id} value={p.id}>{name ? (name.length > 48 ? `${name.slice(0, 48)}…` : name) : `Picture ${i + 1}`}{on ? ` (on post ${on.n})` : ""}</option>;
            })}
          </select>
        </label>
      )}
    </div>
  );
}

/* AN EARLIER POST CHANGED (her ask, 2026-09-30). Amber, because it asks for
   her attention (rule 5), and said under the row it is about. Two ways out:
   Rewrite (the sparkle's own path, which asks first — there is no undo), or
   the ✕ for "it's still fine", which she need not see again until an earlier
   post changes once more. Never rewrites anything by itself. */
function DriftNote({ changed = [], busy, onRewrite, onDismiss }) {
  if (!changed.length) return null;
  const names = changed.length === 1 ? `Post ${changed[0]}` : `Posts ${changed.slice(0, -1).join(", ")} and ${changed[changed.length - 1]}`;
  return (
    <div role="status" style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0 2px 36px", padding: "6px 6px 6px 12px", background: B.amberQuiet, border: `1px solid ${B.amberLine}`, borderRadius: 9, fontSize: 12.5, color: B.text, lineHeight: 1.5 }}>
      <span style={{ flex: 1, minWidth: 0 }}>{names} changed after this was written, so it may not follow on any more.</span>
      <button type="button" onClick={onRewrite} disabled={busy}
        style={{ display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0, padding: "4px 8px", background: "none", border: "none", cursor: busy ? "default" : "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: B.teal, opacity: busy ? 0.5 : 1 }}>
        <Sparkles size={13} /> Rewrite
      </button>
      <IconBtn icon={X} label="It's still fine — hide this" onClick={onDismiss} size={14} />
    </div>
  );
}

/* Under a post's row: what from her Library it is built on, and its picture.
   A pick that can no longer reach Brill (deleted, or a testimonial whose
   permission she has unticked) says so in amber rather than vanishing. */
/* `lead` is the post's date and topic, on a phone only — the header has no
   room for them there, and here the topic wraps rather than being cut. */
function SlotSources({ lead = null, length = null, picks = [], picture = null }) {
  if (!lead && !length && !picks.length && !picture) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 12px", margin: "5px 2px 2px 36px", fontSize: 11.5, color: B.dim, lineHeight: 1.5, minWidth: 0 }}>
      {lead && <span style={{ flexBasis: "100%", fontSize: 12, color: B.mid }}>{lead}</span>}
      {/* A mixed arc says each post's length, so the mix is visible at a glance. */}
      {length && <span><b style={{ fontWeight: 700 }}>{POST_LENGTHS.find((l) => l.id === length)?.label || length}</b></span>}
      {picks.map((p) => (
        <span key={p.id} style={{ color: p.gone ? B.amber : B.dim, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <b style={{ fontWeight: 700 }}>{p.kindLabel}</b>{p.label ? ` · ${p.label}` : ""}{p.gone ? ` — ${p.gone}` : ""}
        </span>
      ))}
      {picture && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <img src={picture.url} alt="" style={{ width: 22, height: 22, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} />
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(picture.about || "").trim() || "Picture"}</span>
        </span>
      )}
    </div>
  );
}
